import { useEffect, useState } from 'react';
import { roundMoney } from '@matix/utils';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import { useGiftCards } from '../../store/giftCards';
import { moneyPrecision } from '../../store/currency';
import { toast } from '../../store/toast';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { CheckIcon } from '../../components/icons';

/**
 * Sell a gift card.
 *
 * Adds ONE cart line, on the tenant's gift-card catalog item, at the card's
 * face value. The card itself does not exist yet — it is created by the order
 * transaction when the sale is charged, so a card can never exist without the
 * receipt that paid for it, and a receipt can never claim to have sold a card it
 * did not create.
 *
 * The number and PIN are generated server-side and shown to the cashier once,
 * after the charge. Nothing in this modal knows them.
 *
 * Self-gates on the 'sellGiftCard' modal id and renders nothing when closed or
 * when the tenant has not nominated a gift-card item.
 */
export function SellGiftCardModal(): JSX.Element | null {
  const { t: tr } = useLang();
  const isOpen = useUI((s) => s.openModal === 'sellGiftCard');
  const closeModal = useUI((s) => s.closeModal);
  const config = useGiftCards((s) => s.config);
  const canSell = useGiftCards((s) => s.canSell);
  const addGiftCardLine = useCart((s) => s.addGiftCardLine);
  const customer = useCart((s) => s.customer);

  const [value, setValue] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // Fresh state each time the modal opens — the previous card is on the cart
  // now, and pre-filling the next one with its recipient would be wrong.
  useEffect(() => {
    if (!isOpen) return;
    setValue(config.denominations[0] != null ? String(config.denominations[0]) : '');
    setRecipientName('');
    setRecipientPhone('');
    setCardNumber('');
    setExpiresAt('');
  }, [isOpen, config.denominations]);

  if (!isOpen) return null;
  if (!canSell()) return null;

  const precision = moneyPrecision();
  const amount = roundMoney(Number(value) || 0, precision);
  const saleItemId = config.sale_item_id;

  function add(): void {
    if (saleItemId == null) return;
    if (!(amount > 0)) {
      toast(tr({ en: 'Choose a value for the card', ar: 'اختر قيمة للبطاقة' }), 'warning');
      return;
    }
    // The same rules the server enforces, checked here so the cashier is told
    // now rather than at the end of the charge. The server is still the
    // authority — this is a courtesy, not the gate.
    const isDenomination = config.denominations.some((d) => Math.abs(d - amount) < 1e-9);
    if (!isDenomination) {
      if (!config.allow_custom_value) {
        toast(
          tr({ en: 'Only the listed values can be sold', ar: 'يمكن بيع القيم المدرجة فقط' }),
          'warning',
        );
        return;
      }
      if (amount < config.min_value) {
        toast(
          tr({
            en: `The minimum gift card is ${fmt(config.min_value)}`,
            ar: `الحد الأدنى للبطاقة ${fmt(config.min_value)}`,
          }),
          'warning',
        );
        return;
      }
      if (config.max_value != null && amount > config.max_value) {
        toast(
          tr({
            en: `The maximum gift card is ${fmt(config.max_value)}`,
            ar: `الحد الأقصى للبطاقة ${fmt(config.max_value)}`,
          }),
          'warning',
        );
        return;
      }
    }

    addGiftCardLine(
      {
        id: saleItemId,
        name: tr({ en: 'Gift card', ar: 'بطاقة هدايا' }),
        nameAr: 'بطاقة هدايا',
        price: amount,
        cat: tr({ en: 'Gift cards', ar: 'بطاقات الهدايا' }),
        emoji: '🎁',
        bg: '#f3e8ff',
        station: '',
      },
      amount,
      {
        recipientName: recipientName.trim() || null,
        recipientPhone: recipientPhone.trim() || null,
        cardNumber: cardNumber.trim() || null,
        expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00`).toISOString() : null,
      },
    );

    toast(
      tr({
        en: `Gift card ${fmt(amount)} added — charge the order to activate it`,
        ar: `تمت إضافة بطاقة هدايا ${fmt(amount)} — أكمل الدفع لتفعيلها`,
      }),
      'success',
    );
    closeModal();
  }

  return (
    <div
      className="modal-overlay show"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: '#f3e8ff', color: '#7e22ce' }}
          >
            🎁
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">{tr({ en: 'Sell a gift card', ar: 'بيع بطاقة هدايا' })}</div>
            <div className="modal-header-name-ar">
              {tr({
                en: 'The card is created when the order is charged.',
                ar: 'يتم إنشاء البطاقة عند إتمام الدفع.',
              })}
            </div>
          </div>
          <button
            className="modal-close"
            onClick={closeModal}
            aria-label={tr({ en: 'Close', ar: 'إغلاق' })}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {config.denominations.length > 0 && (
            <div>
              <div className="pay-label">{tr({ en: 'Value', ar: 'القيمة' })}</div>
              <div className="pay-chips" style={{ marginTop: 6 }}>
                {config.denominations.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="pay-chip"
                    style={
                      Math.abs(amount - d) < 1e-9
                        ? { background: 'var(--primary, #4f46e5)', color: '#fff' }
                        : undefined
                    }
                    onClick={() => setValue(String(d))}
                  >
                    {fmt(d)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {config.allow_custom_value && (
            <label>
              <div className="pay-label">
                {config.denominations.length > 0
                  ? tr({ en: 'Or another amount', ar: 'أو مبلغ آخر' })
                  : tr({ en: 'Amount', ar: 'المبلغ' })}
              </div>
              <input
                className="pay-amount-input"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
              />
            </label>
          )}

          <label>
            <div className="pay-label">{tr({ en: 'Recipient name (optional)', ar: 'اسم المستلم (اختياري)' })}</div>
            <input
              className="pay-giftcard-input"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder={customer?.name ?? ''}
            />
          </label>

          <label>
            <div className="pay-label">{tr({ en: 'Recipient phone (optional)', ar: 'هاتف المستلم (اختياري)' })}</div>
            <input
              className="pay-giftcard-input"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
            />
          </label>

          <label>
            <div className="pay-label">
              {tr({ en: 'Pre-printed card number (optional)', ar: 'رقم بطاقة مطبوعة (اختياري)' })}
            </div>
            <input
              className="pay-giftcard-input"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder={tr({ en: 'Leave blank to generate one', ar: 'اتركه فارغًا للتوليد التلقائي' })}
              autoComplete="off"
            />
          </label>

          <label>
            <div className="pay-label">
              {tr({ en: 'Expires on (optional)', ar: 'تنتهي في (اختياري)' })}
            </div>
            <input
              className="pay-giftcard-input"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <div className="pay-giftcard-left" style={{ marginTop: 4 }}>
              {config.default_expiry_months
                ? tr({
                    en: `Blank uses the default (${config.default_expiry_months} months).`,
                    ar: `الفراغ يستخدم الافتراضي (${config.default_expiry_months} شهرًا).`,
                  })
                : tr({ en: 'Blank = never expires.', ar: 'فارغ = لا تنتهي.' })}
            </div>
          </label>
        </div>

        <div className="modal-footer pay-footer">
          <button className="btn pay-cancel-btn" onClick={closeModal}>
            {tr({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            className="btn btn-primary pay-confirm-btn"
            onClick={add}
            disabled={!(amount > 0)}
          >
            <CheckIcon size={16} />
            {tr({ en: `Add ${fmt(amount)} to cart`, ar: `أضف ${fmt(amount)} للسلة` })}
          </button>
        </div>
      </div>
    </div>
  );
}
